import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { getRequiredMinFrameSpacingForUserKeyPose } from './keyPoseEditDeviation';
import type { IntervalSpacingConfig } from './keyPoseEditDeviation';
import {
  type KeyPoseApiPayloadItem,
  createIntervalKeyPoseId,
} from './keyPoseSelectors';
import { createKeyPoseSourcePosePayloadFromBvh } from './keyPoseSnapshots';
import type { KeyPoseSourcePosePayload } from './types';
import { DEFAULT_KEY_POSE_INTERVAL_FRAMES } from './keyPoseIntervalAugmentationSettings';

export { DEFAULT_KEY_POSE_INTERVAL_FRAMES as KEY_POSE_INTERVAL_FRAMES };

/** Temporary QA toggle: when false, regenerate sends only user keys plus any manually-added interval stamps. */
export const AUTO_AUGMENT_INTERVAL_KEYS_ON_REGENERATE = false;

const LOG_PREFIX = '[keyPoseIntervalAugmentation]';

export type KeyPoseIntervalAugmentationContext = {
  bvh: BVH;
  maxSourceFrame: number;
  intervalFrames: number;
  spacingConfig: IntervalSpacingConfig;
};

export type UserKeyPoseSpacingInput = {
  frame: number;
  sourcePose: KeyPoseSourcePosePayload;
};

export type ComputeDesiredIntervalKeyPoseFramesInput = {
  maxSourceFrame: number;
  intervalFrames: number;
  userKeyPoses: readonly UserKeyPoseSpacingInput[];
  bvh: BVH;
  spacingConfig: IntervalSpacingConfig;
};

export type ReconcileIntervalKeyPoseFramesInput =
  ComputeDesiredIntervalKeyPoseFramesInput & {
    existingIntervalFrames: readonly number[];
  };

export type ReconcileIntervalKeyPoseFramesResult = {
  desiredFrames: number[];
  framesToAdd: number[];
  framesToRemove: number[];
};

export function getMaxSourceFrameFromBvh(bvh: BVH) {
  const keyframeTimes = bvh.clip.tracks[0]?.times;
  if (!keyframeTimes || keyframeTimes.length === 0) {
    return 0;
  }

  return keyframeTimes.length - 1;
}

export function toUserKeyPoseSpacingInput(
  keyPose: Pick<
    KeyPoseApiPayloadItem,
    'frame' | 'localJointRot' | 'rootPosition' | 'smoothRoot2d'
  >,
): UserKeyPoseSpacingInput {
  return {
    frame: keyPose.frame,
    sourcePose: {
      frame: keyPose.frame,
      localJointRot: keyPose.localJointRot,
      rootPosition: keyPose.rootPosition,
      smoothRoot2d: keyPose.smoothRoot2d,
    },
  };
}

export function computeDesiredIntervalKeyPoseFrames({
  maxSourceFrame,
  intervalFrames,
  userKeyPoses,
  bvh,
  spacingConfig,
}: ComputeDesiredIntervalKeyPoseFramesInput) {
  const interval = Math.max(Math.round(intervalFrames), 1);
  const normalizedMaxSourceFrame = Math.max(Math.round(maxSourceFrame), 0);
  const occupiedFrames = new Set(
    userKeyPoses.map((keyPose) => Math.round(keyPose.frame)),
  );
  const frames: number[] = [];

  for (let frame = 0; frame <= normalizedMaxSourceFrame; frame += interval) {
    if (occupiedFrames.has(frame)) {
      continue;
    }

    const tooCloseToUserKeyPose = userKeyPoses.some((userKeyPose) => {
      const userFrame = Math.round(userKeyPose.frame);
      const minDistance = getRequiredMinFrameSpacingForUserKeyPose({
        bvh,
        userSourcePose: userKeyPose.sourcePose,
        intervalFrames: interval,
        config: spacingConfig,
      });

      return Math.abs(userFrame - frame) < minDistance;
    });
    if (tooCloseToUserKeyPose) {
      continue;
    }

    frames.push(frame);
  }

  return frames;
}

export function reconcileIntervalKeyPoseFrames({
  maxSourceFrame,
  intervalFrames,
  userKeyPoses,
  bvh,
  spacingConfig,
  existingIntervalFrames,
}: ReconcileIntervalKeyPoseFramesInput): ReconcileIntervalKeyPoseFramesResult {
  const desiredFrames = computeDesiredIntervalKeyPoseFrames({
    maxSourceFrame,
    intervalFrames,
    userKeyPoses,
    bvh,
    spacingConfig,
  });
  const desiredFrameSet = new Set(desiredFrames);
  const normalizedExistingFrames = existingIntervalFrames.map((frame) =>
    Math.round(frame),
  );
  const existingFrameSet = new Set(normalizedExistingFrames);
  const framesToRemove = normalizedExistingFrames.filter(
    (frame) => !desiredFrameSet.has(frame),
  );
  const framesToAdd = desiredFrames.filter((frame) => !existingFrameSet.has(frame));

  return {
    desiredFrames,
    framesToAdd,
    framesToRemove,
  };
}

export function augmentKeyPosesWithIntervalKeys({
  userKeyPoses,
  existingIntervalKeyPoses = [],
  bvh,
  maxSourceFrame,
  intervalFrames,
  spacingConfig,
}: {
  userKeyPoses: KeyPoseApiPayloadItem[];
  existingIntervalKeyPoses?: KeyPoseApiPayloadItem[];
} & KeyPoseIntervalAugmentationContext): KeyPoseApiPayloadItem[] {
  const { desiredFrames, framesToAdd } = reconcileIntervalKeyPoseFrames({
    maxSourceFrame,
    intervalFrames,
    userKeyPoses: userKeyPoses.map(toUserKeyPoseSpacingInput),
    bvh,
    spacingConfig,
    existingIntervalFrames: existingIntervalKeyPoses.map((keyPose) => keyPose.frame),
  });
  const desiredFrameSet = new Set(desiredFrames);
  const keptIntervalKeyPoses = existingIntervalKeyPoses.filter((keyPose) =>
    desiredFrameSet.has(keyPose.frame),
  );
  const autoKeyPoses: KeyPoseApiPayloadItem[] = [];

  for (const frame of framesToAdd) {
    const sourcePose = createKeyPoseSourcePosePayloadFromBvh(bvh, frame);
    if (!sourcePose) {
      console.warn(
        `${LOG_PREFIX} skipped frame ${frame}: could not capture source pose`,
      );
      continue;
    }

    console.log(`${LOG_PREFIX} key pose added at ${frame} frame`);
    autoKeyPoses.push({
      id: createIntervalKeyPoseId(frame),
      frame,
      localJointRot: sourcePose.localJointRot,
      rootPosition: sourcePose.rootPosition,
      smoothRoot2d: sourcePose.smoothRoot2d,
    });
  }

  return [...userKeyPoses, ...keptIntervalKeyPoses, ...autoKeyPoses].sort(
    (a, b) => a.frame - b.frame,
  );
}
